/** /tour_operators/{id}/lifecycle/… — a tenant's lifecycle (tenant retirement CONTRACT §8.3–8.7,
 *  §10): close, release (code) and reopen, each a request ops make and another step approves.
 *
 *  Reading needs `console_tenant_lifecycle_view`, every write `console_tenant_lifecycle_manage`
 *  (Ops Admin only); the backend is the authoritative check. With two-person approval on
 *  (`two_person_required`) the requester cannot approve their own request (403 `self_approval`).
 *  A close, and a reopen of a closed tenant, apply at approval (200); a release and a reopen of a
 *  released tenant start a job (202, the request `applying`; its progress is the request's
 *  `lifecycle.release` / `lifecycle.restore`). A failed or stopped job runs again from its stored
 *  step (`retry`, 202). `release_preview` says what a release does before it is approved.
 *
 *  Refusals answer `TenantLifecycleError` `{detail, code, …extra}`; `lifecycleErrorWords` puts
 *  every code into words. Shapes come from the generated spec types. */
import { formatDate } from '@/utils/format';

import { ConsoleApiError, apiGet, apiPost } from './client';
import type { TenantRequest } from './tenantRequests';
import type { components } from './types';

type Schemas = components['schemas'];

export type TenantLifecycle = Schemas['TenantLifecycle'];
export type TenantLifecycleRefusal = Schemas['TenantLifecycleRefusal'];
export type TenantReleasePreview = Schemas['TenantReleasePreview'];
export type TenantReleaseCodeState = Schemas['TenantReleaseCodeState'];
/** `GET …/lifecycle/`; `reopen_available` / `reopen_blockers` say whether a reopen may be asked now
 *  (a released tenant: only while its codes are free — `code_taken` names the `holders`). */
export type TenantLifecycleState = Schemas['TenantLifecycleState'];
export type TenantLifecycleRequestCreate = Schemas['TenantLifecycleRequestCreate'];
export type TenantLifecycleDecision = Schemas['TenantLifecycleDecision'];
export type TenantLifecycleErrorBody = Schemas['TenantLifecycleError'];
export type TenantLifecycleErrorCode = TenantLifecycleErrorBody['code'];
export type TenantLifecycleRequestKind = TenantLifecycleRequestCreate['kind'];

/** A reason, an approval reason or a rejection note: the backend's limit. */
export const LIFECYCLE_REASON_MAX = 2000;

/** Under `tourOperators`, so a tenant-wide invalidation refreshes it too. */
export const lifecycleKeys = {
  state: (topId: number) => ['tourOperators', 'lifecycle', topId] as const,
  /** Under the state's key: re-reading the state re-reads an open preview too. */
  releasePreview: (topId: number) => ['tourOperators', 'lifecycle', topId, 'releasePreview'] as const,
};

const base = (topId: number) => `/tour_operators/${topId}/lifecycle/`;

export const lifecycleApi = {
  state(topId: number): Promise<TenantLifecycleState> {
    return apiGet(base(topId));
  },
  /** 201: the new request, `pending`. */
  request(topId: number, body: TenantLifecycleRequestCreate): Promise<TenantRequest> {
    return apiPost(`${base(topId)}requests/`, body);
  },
  /** 200: applied (close, reopen of a closed tenant); 202: a job started (`applying`). */
  approve(topId: number, requestId: number, body: TenantLifecycleDecision): Promise<TenantRequest> {
    return apiPost(`${base(topId)}requests/${requestId}/approve/`, body);
  },
  reject(topId: number, requestId: number, note: string): Promise<TenantRequest> {
    return apiPost(`${base(topId)}requests/${requestId}/reject/`, { note });
  },
  withdraw(topId: number, requestId: number): Promise<TenantRequest> {
    return apiPost(`${base(topId)}requests/${requestId}/withdraw/`, {});
  },
  /** 202: a failed (or stopped) release / restore job runs again from its stored step. */
  retry(topId: number, requestId: number): Promise<TenantRequest> {
    return apiPost(`${base(topId)}requests/${requestId}/retry/`, {});
  },
  /** What a release does (or, while one runs, is doing): counts are estimates, the job is exact. */
  releasePreview(topId: number): Promise<TenantReleasePreview> {
    return apiGet(`${base(topId)}release_preview/`);
  },
};

/** One manual follow-up of a reopen (`lifecycle.reprovision.manual`): what cannot be
 *  re-provisioned automatically — `what` a key (`email_domain`, `twilio_numbers`, `api_keys`, …),
 *  `detail` the sentence saying what to do. */
export interface ReopenFollowUp {
  what: string;
  detail: string;
}

/** A reopen's manual follow-ups, recorded once the tenant is active again; [] before (or for any
 *  other request). The spec leaves the block open (`additionalProperties`), so it is read
 *  defensively. */
export function reopenFollowUps(request: TenantRequest): ReopenFollowUp[] {
  const manual = request.lifecycle?.reprovision?.manual;
  if (!Array.isArray(manual)) return [];
  return manual.flatMap((m): ReopenFollowUp[] => {
    if (typeof m === 'string') return m ? [{ what: '', detail: m }] : [];
    if (m && typeof m === 'object' && typeof (m as { what?: unknown }).what === 'string') {
      const { what, detail } = m as { what: string; detail?: unknown };
      return [{ what, detail: typeof detail === 'string' ? detail : '' }];
    }
    return [];
  });
}

/** The refusal body of a lifecycle endpoint, or null for any other error. */
export function lifecycleError(e: unknown): TenantLifecycleErrorBody | null {
  if (!(e instanceof ConsoleApiError)) return null;
  const body = e.body as Partial<TenantLifecycleErrorBody> | undefined;
  return typeof body?.code === 'string' && body.code in LIFECYCLE_ERROR_WORDS
    ? (body as TenantLifecycleErrorBody)
    : null;
}

const STATUS_WORDS: Record<string, string> = {
  pending: 'pending',
  approved: 'approved',
  applying: 'being applied',
  applied: 'applied',
  rejected: 'rejected',
  withdrawn: 'withdrawn',
  superseded: 'superseded',
  failed: 'failed',
};

const KIND_WORDS: Record<TenantLifecycleRequestKind, string> = {
  close: 'close',
  release: 'code release',
  reopen: 'reopen',
};

/** Every `TenantLifecycleError.code` in words; `satisfies` keeps it exhaustive against the spec. */
const LIFECYCLE_ERROR_WORDS = {
  not_active: (b) =>
    `Only an active tenant can be closed — this one is ${b.lifecycle ?? 'not active'} now.`,
  // Without `lifecycle` it is a step that is not available yet (the backend's detail says which).
  not_closed: (b) =>
    b.lifecycle
      ? `This needs a closed tenant — this one is ${b.lifecycle} now.`
      : `Not possible now: ${(b.detail || 'the tenant is not closed').replace(/\.$/, '')}.`,
  lifecycle_in_progress: () =>
    'A release or a restore is running on this tenant — wait until it has finished.',
  lifecycle_request_open: (b) =>
    `Another lifecycle request is open for this tenant${
      b.kind ? ` (${KIND_WORDS[b.kind]}${b.status ? `, ${STATUS_WORDS[b.status] ?? b.status}` : ''})` : ''
    } — decide or withdraw it first.`,
  release_too_early: (b) =>
    `The code can be released from ${formatDate(b.earliest_release_at)}${
      b.min_closed_days != null ? ` (${b.min_closed_days} days after the close)` : ''
    }.`,
  override_not_allowed: () => 'Only a superuser may override the release waiting period.',
  offboarding_incomplete: (b) => {
    const titles = (b.items ?? []).map((i) => i.title ?? i.action).filter(Boolean);
    return `The offboarding checklist is not finished${titles.length ? `: ${titles.join(', ')}` : ''}.`;
  },
  work_running: (b) =>
    `Work is still running for this tenant${b.what?.length ? ` (${b.what.join(', ')})` : ''} — try again when it has finished.`,
  paired_sandbox: () =>
    'This is the sandbox of a production tenant: it is released with that tenant, never on its own.',
  not_retryable: (b) =>
    `There is nothing to run again${
      b.lifecycle && b.lifecycle !== 'releasing' && b.lifecycle !== 'restoring'
        ? ` — the tenant is ${b.lifecycle} now`
        : b.status
          ? ` — the request is ${STATUS_WORDS[b.status] ?? b.status}`
          : ''
    }: only a failed or stopped release or restore runs again, and not while its job is still alive.`,
  code_taken: (b) => {
    const codes = (b.holders ?? []).map((h) => h.code).filter(Boolean);
    return `The code is held by another tenant now${codes.length ? `: ${codes.join(', ')}` : ''}.`;
  },
  self_approval: () =>
    'Two-person approval is on: someone other than the requester has to approve this request.',
  confirm_code_mismatch: () => 'The code you typed is not the tenant’s code (it is case-sensitive).',
  reason_required: () => 'Say why — a reason is required.',
  wrong_kind: () => 'This is not a lifecycle request — decide it on the Tenant requests page.',
  request_not_pending: (b) =>
    `This request is no longer pending${b.status ? ` — it is ${STATUS_WORDS[b.status] ?? b.status} now` : ''}.`,
} satisfies Record<TenantLifecycleErrorCode, (body: TenantLifecycleErrorBody) => string>;

/** A lifecycle refusal in plain words: the code's own sentence; a 403 without one is the
 *  permission; otherwise the backend's detail, else `fallback`. */
export function lifecycleErrorWords(e: unknown, fallback = 'That did not go through — try again.'): string {
  const body = lifecycleError(e);
  if (body) return LIFECYCLE_ERROR_WORDS[body.code](body);
  if (e instanceof ConsoleApiError && e.status === 403)
    return 'This needs the Manage tenant lifecycle permission.';
  if (e instanceof ConsoleApiError && e.status === 404)
    return 'The tenant or the request no longer exists.';
  if (e instanceof ConsoleApiError && typeof e.body?.detail === 'string' && e.body.detail)
    return e.body.detail;
  return fallback;
}
